import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "../../styles/LinkTreeView.css";

interface LinkTreeItem {
  id: string;
  title: string;
  url: string;
  order: number;
}

interface PublicLinkTree {
  name: string;
  items: LinkTreeItem[];
  totalViews: number;
}

interface RouteParams extends Record<string, string | undefined> {
  cgiCode?: string;
}

const SortableLinkCard: React.FC<{
  item: LinkTreeItem;
  onLinkClick: (itemId: string, url: string) => void;
  getDomainFromUrl: (url: string) => string;
}> = ({ item, onLinkClick, getDomainFromUrl }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`linktree-link-card ${isDragging ? "dragging" : ""}`}
    >
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="9" cy="12" r="1" fill="currentColor" />
          <circle cx="15" cy="12" r="1" fill="currentColor" />
          <circle cx="9" cy="6" r="1" fill="currentColor" />
          <circle cx="15" cy="6" r="1" fill="currentColor" />
          <circle cx="9" cy="18" r="1" fill="currentColor" />
          <circle cx="15" cy="18" r="1" fill="currentColor" />
        </svg>
      </div>
      <div
        className="link-content"
        onClick={() => onLinkClick(item.id, item.url)}
      >
        <div className="link-info">
          <h3 className="link-title">{item.title}</h3>
          <div className="link-domain">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M2 12h20" stroke="currentColor" strokeWidth="2" />
              <path
                d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span className="domain-text" title={getDomainFromUrl(item.url)}>
              {getDomainFromUrl(item.url)}
            </span>
          </div>
        </div>
        <div className="link-arrow">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 17L17 7M17 7H7M17 7V17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

const LinkTreeView: React.FC = () => {
  const { cgiCode } = useParams<RouteParams>();
  const shortCode = cgiCode?.startsWith("CGI-") ? cgiCode.substring(4) : undefined;
  const [linkTree, setLinkTree] = useState<PublicLinkTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<LinkTreeItem[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchPublicLinkTree = useCallback(async () => {
    try {
      setLoading(true);
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${backendUrl}/CGI-${shortCode}`);

      if (response.ok) {
        const data: PublicLinkTree = await response.json();
        setLinkTree(data);
        setError(null);
      } else if (response.status === 404) {
        setError("LinkTree not found");
        setLinkTree(null);
      } else {
        setError("Failed to load LinkTree");
        setLinkTree(null);
      }
    } catch (err) {
      setError("Failed to load LinkTree");
      setLinkTree(null);
    } finally {
      setLoading(false);
    }
  }, [shortCode]);

  useEffect(() => {
    if (shortCode) {
      fetchPublicLinkTree();
    } else {
      setLoading(false);
      setError("No short code provided");
    }
  }, [shortCode, fetchPublicLinkTree]);

  useEffect(() => {
    if (linkTree?.items) {
      const processedItems = linkTree.items
        .filter(
          (item, index, self) =>
            index ===
            self.findIndex((t) => t.title === item.title && t.url === item.url)
        )
        .map((item, index) => ({
          ...item,
          id: item.id.includes("item-")
            ? item.id
            : `item-${index}-${Date.now()}`,
        }))
        .sort((a, b) => a.order - b.order);

      setItems(processedItems);
    }
  }, [linkTree]);

  const handleLinkClick = async (itemId: string, url: string) => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      let cleanItemId = itemId;
      if (itemId.includes("-")) {
        const parts = itemId.split("-");
        cleanItemId = parts.length === 3 ? parts[1] : parts[parts.length - 1];
      }

      await fetch(`${backendUrl}/linktree/track-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shortCode,
          itemId: cleanItemId,
        }),
      });
    } catch (err) {}

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      setItems(updatedItems);

      try {
        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const response = await fetch(`${backendUrl}/linktree/reorder`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shortCode,
            items: updatedItems.map((item) => ({
              id: item.id.startsWith("item-") ? item.id.split("-")[1] : item.id,
              order: item.order,
            })),
          }),
        });

        if (!response.ok) {
          setItems(items);
        }
      } catch (err) {
        setItems(items);
      }
    }
  };

  const getDomainFromUrl = (url: string): string => {
    try {
      const domain = new URL(url).hostname;
      return domain.startsWith("www.") ? domain.slice(4) : domain;
    } catch {
      return url;
    }
  };

  const getInitials = (name: string): string => {
    const words = name.split(" ").filter((word) => word.length > 0);

    if (words.length === 1) {
      return (
        words[0].charAt(0).toUpperCase() +
        (words[0].charAt(1) || "").toUpperCase()
      );
    } else {
      const firstInitial = words[0].charAt(0).toUpperCase();
      const lastInitial = words[words.length - 1].charAt(0).toUpperCase();
      return firstInitial + lastInitial;
    }
  };

  if (loading) {
    return (
      <div className="linktree-public-loading">
        <div className="cgi-logo">
          <img src="/images/cgi-logo.png" alt="CGI" className="logo-img" />
        </div>
        <div className="loading-spinner"></div>
        <p>Loading LinkHub...</p>
      </div>
    );
  }

  if (error || !linkTree) {
    return (
      <div className="linktree-public-error">
        <div className="error-icon">🔗</div>
        <h2>LinkHub Not Found</h2>
        <p>
          {error ||
            "This LinkHub may have been removed or the link is incorrect."}
        </p>
        <button
          className="error-button"
          onClick={() => (window.location.href = "/")}
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="linktree-public-container">
      <div className="linktree-card-container">
        <div className="linktree-public-header">
          <div className="profile-avatar">{getInitials(linkTree.name)}</div>
          <h1 className="linktree-title">{linkTree.name}</h1>
          <div className="linktree-public-content">
            {items.length === 0 ? (
              <div className="no-links">
                <div className="empty-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M8 12h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <p>No links available</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((item) => item.id)}
                  strategy={
                    window.innerWidth <= 768
                      ? verticalListSortingStrategy
                      : rectSortingStrategy
                  }
                >
                  <div className="linktree-links">
                    {items.map((item) => (
                      <SortableLinkCard
                        key={item.id}
                        item={item}
                        onLinkClick={handleLinkClick}
                        getDomainFromUrl={getDomainFromUrl}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
        <div className="linktree-public-footer">
          <div className="powered-by">
            <span>Powered by CGI</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkTreeView;
